import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  MagnifyingGlass, PaperPlaneRight, Copy, CheckCircle, Clock, Warning, DotsThreeVertical,
  Plus, X, Users, UsersThree, UploadSimple, ChatText, Trash, PencilSimple, ArrowClockwise,
  Smiley, ChatCircleText, Bell, PushPin, House, ShieldCheck, Tray, Flame, Bank,
  Clipboard, PaperPlaneTilt, Wallet, Key, SignOut, DownloadSimple,
  Paperclip, Image as ImageIcon, FilePdf, FileXls, FileCsv, FileDoc, FileZip, File as FileIcon,
} from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';

/* ── Date/Time helpers (WhatsApp-style) ── */
function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
}
function fmtDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(yest.getDate() - 1);
  const sameDay = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yest)) return 'Yesterday';
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}
function dayKey(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function DateSeparator({ label }) {
  return (
    <div className="flex justify-center my-3" data-testid={`date-sep-${label}`}>
      <div className="bg-white/90 border border-gray-200 text-gray-600 text-[10px] font-semibold px-2.5 py-0.5 rounded-full shadow-sm uppercase tracking-wider">
        {label}
      </div>
    </div>
  );
}

const REACTIONS = ['👍', '❤️', '😂', '😮', '🙏', '🔥'];

/* ── Status pill helpers ── */
function StatusPill({ status, onClick, size = 'xs' }) {
  const map = {
    pending: { bg: 'bg-amber-100', text: 'text-amber-800', label: '⏳ Pending', border: 'border-amber-300' },
    delivered: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: '✓ Delivered', border: 'border-emerald-400' },
    failed: { bg: 'bg-rose-100', text: 'text-rose-800', label: '✗ Failed', border: 'border-rose-400' },
    read: { bg: 'bg-emerald-200', text: 'text-emerald-900', label: '✓✓ Read', border: 'border-emerald-500' },
  };
  const c = map[status] || map.pending;
  const sz = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-[9px] px-1.5 py-0.5';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${c.bg} ${c.text} border ${c.border} rounded font-bold ${sz} ${onClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
    >
      {c.label}
    </button>
  );
}

/* ── LPG card bubble (standalone — no batch) ── */
function LpgCardBubble({ msg, own, onStatusCycle }) {
  const sd = msg.structured_data || {};
  const hasConsumer = !!sd.consumer_number;
  const copyAll = async () => {
    const text = hasConsumer
      ? `${sd.consumer_number}${sd.dac ? '\nDAC ' + sd.dac : ''}${sd.mobile ? '\nMob ' + sd.mobile : ''}`
      : `${sd.mobile || ''}`;
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch { /* empty */ }
  };
  const status = msg.delivery_status || 'pending';
  const cycleStatus = () => {
    const next = status === 'pending' ? 'delivered' : status === 'delivered' ? 'failed' : 'pending';
    onStatusCycle?.(msg.id, next);
  };
  return (
    <div className={`rounded-lg p-2.5 border-2 ${own ? 'bg-emerald-900/90 border-emerald-700 text-white' : 'bg-white border-emerald-300 text-gray-900'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[13px] font-mono font-bold tracking-wide">{sd.consumer_number || sd.mobile || '—'}</div>
        <StatusPill status={status} onClick={cycleStatus} />
      </div>
      {(sd.dac || (sd.mobile && hasConsumer)) && (
        <div className="text-[10px] mt-0.5 flex gap-2">
          {sd.dac && <span><span className="opacity-60">DAC</span> <b>{sd.dac}</b></span>}
          {sd.mobile && hasConsumer && <span><span className="opacity-60">Mob</span> <b>{sd.mobile}</b></span>}
        </div>
      )}
      <button onClick={copyAll} className={`mt-1 text-[10px] flex items-center gap-1 ${own ? 'text-emerald-200 hover:text-white' : 'text-emerald-700 hover:text-emerald-900'}`} data-testid={`msg-copy-${msg.id}`}>
        <Copy size={10} /> Copy
      </button>
      {msg.is_duplicate && (
        <div className="mt-1 text-[9px] text-rose-500 flex items-center gap-1"><Warning size={9} /> Duplicate — original still active</div>
      )}
    </div>
  );
}

/* ── LPG Delivery grouped batch bubble ── */
function LpgBatchBubble({ batchMsgs, onStatusCycle, apiBase }) {
  const first = batchMsgs[0];
  const total = batchMsgs.length;
  const done = batchMsgs.filter(m => (m.delivery_status || 'pending') === 'delivered').length;
  const failed = batchMsgs.filter(m => (m.delivery_status || 'pending') === 'failed').length;
  const pending = total - done - failed;
  const agency = first.agency_name || '';
  const batchId = first.batch_id;

  const copyAll = async () => {
    const lines = batchMsgs.map(m => (m.structured_data?.mobile || m.structured_data?.consumer_number || '')).filter(Boolean);
    const text = [agency, ...lines].filter(Boolean).join('\n');
    try { await navigator.clipboard.writeText(text); toast.success(`Copied ${lines.length} rows`); } catch { /* empty */ }
  };

  const downloadBatch = async () => {
    try {
      const res = await api.get(`/chat/batches/${batchId}/export`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LPG_Delivery_${agency.replace(/[^A-Za-z0-9]/g, '_')}_${batchId.slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch { toast.error('Download failed'); }
  };

  return (
    <div className="max-w-md border-2 border-emerald-700 bg-emerald-50 rounded-lg overflow-hidden shadow-sm" data-testid={`lpg-batch-${batchId}`}>
      {/* Header */}
      <div className="bg-emerald-700 text-white px-2.5 py-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Clipboard size={12} weight="bold" />
          <div className="text-[11px] font-bold uppercase tracking-wide truncate">LPG DELIVERY</div>
          <div className="text-[10px] opacity-90 truncate">— {agency || 'Agency'}</div>
        </div>
        <div className="text-[10px] font-bold shrink-0">
          <span className={done === total ? 'text-emerald-200' : 'text-amber-200'}>{done}</span>
          <span className="opacity-70">/{total}</span>
        </div>
      </div>
      {/* Rows */}
      <div className="max-h-80 overflow-y-auto bg-white">
        {batchMsgs.map((m, idx) => {
          const sd = m.structured_data || {};
          const mobile = sd.mobile || sd.consumer_number || '';
          const status = m.delivery_status || 'pending';
          const cycle = () => {
            const next = status === 'pending' ? 'delivered' : status === 'delivered' ? 'failed' : 'pending';
            onStatusCycle(m.id, next);
          };
          const copyRow = async () => {
            try { await navigator.clipboard.writeText(mobile); toast.success('Copied'); } catch { /* empty */ }
          };
          return (
            <div
              key={m.id}
              className={`flex items-center gap-2 px-2.5 py-1 text-xs border-b border-gray-100 last:border-b-0 ${status === 'failed' ? 'bg-rose-50' : status === 'delivered' ? 'bg-emerald-50/40' : ''}`}
              data-testid={`lpg-batch-row-${m.id}`}
            >
              <span className="text-[9px] text-gray-400 w-5 shrink-0 text-right">{idx + 1}</span>
              <span className={`text-[11px] font-mono font-bold flex-1 ${status === 'failed' ? 'text-rose-700' : 'text-gray-800'}`}>
                {mobile}
              </span>
              <StatusPill status={status} onClick={cycle} size="xs" />
              <button onClick={copyRow} className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500" title="Copy mobile">
                <Copy size={10} />
              </button>
            </div>
          );
        })}
      </div>
      {/* Footer */}
      <div className="bg-amber-100 border-t border-amber-200 px-2.5 py-1.5 flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold text-amber-900">
          {done > 0 && <><span className="text-emerald-700">{done} Done</span> · </>}
          {failed > 0 && <><span className="text-rose-700">{failed} Failed</span> · </>}
          <span className="text-amber-800">{pending} Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={copyAll} className="text-[10px] px-2 py-0.5 rounded bg-white border border-amber-300 hover:bg-amber-50 text-amber-900 font-bold flex items-center gap-0.5" title="Copy all rows" data-testid={`lpg-batch-copy-${batchId}`}>
            <Copy size={10} /> Copy All
          </button>
          <button onClick={downloadBatch} className="text-[10px] px-2 py-0.5 rounded bg-emerald-700 hover:bg-emerald-800 text-white font-bold flex items-center gap-0.5" title="Download Excel" data-testid={`lpg-batch-dl-${batchId}`}>
            <DownloadSimple size={10} weight="bold" /> Excel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Text bubble ── */
function TextBubble({ msg, own }) {
  return (
    <div className={`rounded-lg px-3 py-1.5 ${own ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
      {msg.reply_to_snippet && (
        <div className={`text-[10px] italic border-l-2 pl-1.5 mb-1 ${own ? 'border-emerald-300 text-emerald-100' : 'border-emerald-500 text-gray-500'}`}>
          {msg.reply_to_snippet}
        </div>
      )}
      <div className="text-[13px] whitespace-pre-wrap break-words">{msg.content}</div>
    </div>
  );
}

/* ── Image bubble (WhatsApp-style thumbnail with lightbox) ── */
function ImageBubble({ msg, own, onOpenLightbox }) {
  const sd = msg.structured_data || {};
  // Same-origin: /api/chat/files/{id}?inline=1 is proxied to backend by ingress; cookies auto-send
  const srcUrl = `/api/chat/files/${sd.file_id}?inline=1`;
  return (
    <div className={`rounded-lg overflow-hidden ${own ? 'bg-emerald-600' : 'bg-white border border-gray-200'}`} data-testid={`image-bubble-${msg.id}`}>
      <button
        type="button"
        onClick={() => onOpenLightbox?.(srcUrl, sd.filename)}
        className="block max-w-xs"
      >
        <img
          src={srcUrl}
          alt={sd.filename || 'image'}
          className="max-h-72 w-auto rounded-t-lg object-cover"
          loading="lazy"
        />
      </button>
      {msg.content && (
        <div className={`px-2 py-1 text-[12px] whitespace-pre-wrap break-words ${own ? 'text-white' : 'text-gray-900'}`}>
          {msg.content}
        </div>
      )}
    </div>
  );
}

/* ── File bubble (PDF/Excel/CSV/Word/Zip/other) ── */
function FileBubble({ msg, own }) {
  const sd = msg.structured_data || {};
  const cat = sd.category || 'file';
  const iconMap = {
    pdf:   { Icon: FilePdf, color: 'text-rose-600',   bg: 'bg-rose-50',   label: 'PDF' },
    excel: { Icon: FileXls, color: 'text-emerald-700', bg: 'bg-emerald-50', label: 'XLS' },
    csv:   { Icon: FileCsv, color: 'text-emerald-700', bg: 'bg-emerald-50', label: 'CSV' },
    word:  { Icon: FileDoc, color: 'text-blue-700',   bg: 'bg-blue-50',   label: 'DOC' },
    zip:   { Icon: FileZip, color: 'text-amber-700',  bg: 'bg-amber-50',  label: 'ZIP' },
    file:  { Icon: FileIcon, color: 'text-gray-700',  bg: 'bg-gray-50',   label: 'FILE' },
  };
  const { Icon, color, bg, label } = iconMap[cat] || iconMap.file;
  const dl = async () => {
    try {
      const res = await api.get(`/chat/files/${sd.file_id}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: sd.mime || 'application/octet-stream' });
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u; a.download = sd.filename || 'download'; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch { toast.error('Download failed'); }
  };
  return (
    <div className={`rounded-lg overflow-hidden ${own ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-900'}`} data-testid={`file-bubble-${msg.id}`}>
      <button
        type="button"
        onClick={dl}
        className="flex items-center gap-2.5 px-2.5 py-2 w-full max-w-xs"
      >
        <div className={`w-10 h-10 rounded ${bg} ${color} flex items-center justify-center shrink-0`}>
          <Icon size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[12px] font-semibold truncate">{sd.filename || 'file'}</div>
          <div className={`text-[10px] ${own ? 'text-emerald-100' : 'text-gray-500'}`}>
            {label} · {fmtBytes(sd.size)}
          </div>
        </div>
        <DownloadSimple size={14} className={own ? 'text-emerald-100' : 'text-gray-500'} />
      </button>
      {msg.content && (
        <div className={`px-2.5 pb-1.5 text-[12px] whitespace-pre-wrap break-words ${own ? 'text-white' : 'text-gray-900'}`}>
          {msg.content}
        </div>
      )}
    </div>
  );
}

/* ── Tombstone bubble (shown when message is deleted-for-everyone) ── */
function TombstoneBubble({ msg, own }) {
  const byName = msg.deleted_by_name || '';
  const bySelf = (msg.deleted_by_name && msg.sender_name && byName === msg.sender_name) || false;
  const label = bySelf
    ? `This message was deleted`
    : (byName ? `This message was deleted by ${byName}` : 'This message was deleted');
  return (
    <div className={`rounded-lg px-3 py-1.5 italic ${own ? 'bg-emerald-900/40 text-emerald-100' : 'bg-gray-100 text-gray-500'} border border-dashed ${own ? 'border-emerald-700' : 'border-gray-300'}`} data-testid={`tombstone-${msg.id}`}>
      <div className="text-[12px] flex items-center gap-1">
        <Trash size={11} className="opacity-70" />
        <span>{label}</span>
      </div>
    </div>
  );
}

/* ── Message row ── */
function MessageRow({ msg, own, currentUserId, currentUserRole, activeGroup, onReply, onReact, onDelete, onCopy, onStatusCycle, onOpenLightbox }) {
  const [showActions, setShowActions] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showDelMenu, setShowDelMenu] = useState(false);
  const isTombstoned = (msg.deleted_scope === 'everyone') || (!!msg.deleted_at);
  // Permission: delete for everyone
  const isAppOwner = ['admin', 'owner'].includes(currentUserRole);
  const isGroupAdmin = !!(activeGroup?.members || []).find(mm => mm.user_id === currentUserId && mm.role === 'admin');
  const createdMs = new Date(msg.created_at).getTime();
  const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
  const withinWindow = Number.isFinite(createdMs) && (Date.now() - createdMs) <= twoDaysMs;
  const isSender = msg.sender_id === currentUserId;
  const canDeleteForEveryone = isAppOwner || isGroupAdmin || (isSender && withinWindow);

  return (
    <div
      className={`flex gap-1.5 group ${own ? 'flex-row-reverse' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { setShowActions(false); setShowReactions(false); setShowDelMenu(false); }}
      data-testid={`msg-row-${msg.id}`}
    >
      <div className={`max-w-[76%] ${own ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
        {!own && msg.type !== 'system' && !isTombstoned && (
          <div className="text-[10px] font-bold text-violet-700 ml-2">{msg.sender_name} · <span className="text-gray-400 font-normal">{msg.sender_role}</span></div>
        )}
        {isTombstoned ? (
          <TombstoneBubble msg={msg} own={own} />
        ) : (
          <>
            {msg.type === 'lpg_card' && <LpgCardBubble msg={msg} own={own} onStatusCycle={onStatusCycle} />}
            {msg.type === 'image' && <ImageBubble msg={msg} own={own} onOpenLightbox={onOpenLightbox} />}
            {msg.type === 'file' && <FileBubble msg={msg} own={own} />}
            {(msg.type === 'text' || msg.type === 'system' || !['lpg_card', 'image', 'file'].includes(msg.type)) && <TextBubble msg={msg} own={own} />}
          </>
        )}
        <div className="flex items-center gap-1 px-1">
          <span className="text-[9px] text-gray-400" title={formatDateTime(msg.created_at)}>{fmtTime(msg.created_at)}</span>
          {!isTombstoned && msg.reactions && msg.reactions.length > 0 && (
            <div className="flex gap-0.5">
              {Object.entries(msg.reactions.reduce((a, r) => { a[r.emoji] = (a[r.emoji] || 0) + 1; return a; }, {})).map(([e, c]) => (
                <span key={e} className="text-[10px] bg-white border border-gray-200 rounded-full px-1">{e} {c > 1 ? c : ''}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {showActions && !isTombstoned && (
        <div className={`flex flex-col gap-0.5 self-center relative ${own ? 'items-end' : ''}`}>
          <button onClick={() => onReply(msg)} className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-violet-50 text-gray-600" title="Reply" data-testid={`msg-reply-${msg.id}`}>
            <ChatCircleText size={11} />
          </button>
          <button onClick={() => setShowReactions(v => !v)} className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-violet-50 text-gray-600" title="React" data-testid={`msg-react-${msg.id}`}>
            <Smiley size={11} />
          </button>
          <button
            onClick={() => setShowDelMenu(v => !v)}
            className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-rose-50 text-rose-500"
            title="Delete"
            data-testid={`msg-del-${msg.id}`}
          >
            <Trash size={11} />
          </button>
          {showDelMenu && (
            <div
              className={`absolute top-full ${own ? 'right-7' : 'left-7'} mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 min-w-[180px] overflow-hidden`}
              data-testid={`del-menu-${msg.id}`}
            >
              <button
                onClick={() => { setShowDelMenu(false); onDelete(msg, 'me'); }}
                className="w-full text-left px-3 py-2 hover:bg-rose-50 text-rose-700 text-xs flex items-center gap-2"
                data-testid={`del-for-me-${msg.id}`}
              >
                <Trash size={12} /> Delete for me
              </button>
              {canDeleteForEveryone && (
                <button
                  onClick={() => { setShowDelMenu(false); onDelete(msg, 'everyone'); }}
                  className="w-full text-left px-3 py-2 hover:bg-rose-50 text-rose-700 text-xs flex items-center gap-2 border-t border-gray-100"
                  data-testid={`del-for-all-${msg.id}`}
                >
                  <Trash size={12} weight="fill" /> Delete for everyone
                </button>
              )}
            </div>
          )}
          {showReactions && (
            <div className="absolute mt-8 bg-white border border-gray-200 rounded-full shadow-lg flex gap-0.5 px-1.5 py-1 z-10">
              {REACTIONS.map(e => (
                <button key={e} onClick={() => { onReact(msg, e); setShowReactions(false); }} className="text-base hover:scale-125 transition-transform">{e}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Groups list (left column) ── */
function GroupsList({ groups, activeGroupId, onSelect, filter, setFilter, search, setSearch, onNewGroup, isPrivileged }) {
  return (
    <div className="w-80 border-r border-gray-200 bg-gray-50 flex flex-col" data-testid="chat-groups-panel">
      <div className="p-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <ChatText size={18} className="text-violet-600" weight="fill" />
            <span className="text-sm font-bold text-gray-800">Groups · {groups.length}</span>
          </div>
          {isPrivileged && (
            <button onClick={onNewGroup} className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="chat-new-group-btn">
              <Plus size={14} weight="bold" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-full px-2 py-1">
          <MagnifyingGlass size={12} className="text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search groups…" className="bg-transparent outline-none text-xs flex-1" data-testid="chat-groups-search" />
        </div>
        <div className="flex gap-1 mt-2">
          {[{ k: '', label: `All ${groups.length}` }, { k: 'unread', label: 'Unread' }, { k: 'favourites', label: 'Favourites' }].map(t => (
            <button key={t.k} onClick={() => setFilter(t.k)} className={`text-[10px] px-2 py-0.5 rounded-full border ${filter === t.k ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-testid={`chat-filter-${t.k || 'all'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {groups.length === 0 && <div className="p-8 text-center text-xs text-gray-500">No groups yet.</div>}
        {groups.map(g => (
          <button
            key={g.id}
            onClick={() => onSelect(g)}
            className={`w-full text-left p-3 border-b border-gray-100 hover:bg-white transition-colors flex items-start gap-2.5 ${activeGroupId === g.id ? 'bg-white border-l-4 border-l-emerald-600' : ''}`}
            data-testid={`chat-group-${g.id}`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white text-sm shrink-0 ${activeGroupId === g.id ? 'bg-emerald-600' : 'bg-violet-500'}`}>
              {(g.name || '?').substring(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <div className="text-xs font-bold text-gray-800 truncate">{g.name}</div>
                {g.last_message_at && <div className="text-[9px] text-gray-400 shrink-0">{formatDateTime(g.last_message_at)}</div>}
              </div>
              <div className="flex items-center justify-between gap-1 mt-0.5">
                <div className="text-[11px] text-gray-500 truncate">
                  {g.last_message_preview || <span className="italic">No messages yet</span>}
                </div>
                {g.unread_count > 0 && (
                  <span className="text-[9px] bg-emerald-600 text-white font-bold rounded-full px-1.5 py-0.5 shrink-0" data-testid={`chat-unread-${g.id}`}>
                    {g.unread_count > 99 ? '99+' : g.unread_count}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Group Info panel (right) ── */
function GroupInfoPanel({ group, stats, onClose, onOpenBulkPaste, isPrivileged, onRefresh }) {
  if (!group) return null;
  return (
    <div className="w-72 border-l border-gray-200 bg-white flex flex-col" data-testid="chat-group-info">
      <div className="p-3 border-b border-gray-200 flex items-center justify-between">
        <div className="text-sm font-bold text-gray-800 flex items-center gap-1.5"><UsersThree size={16} className="text-emerald-600" weight="bold" /> Group Info</div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
      </div>
      <div className="p-3 border-b border-gray-100">
        <div className="w-20 h-20 rounded-2xl bg-emerald-600 text-white font-bold text-2xl flex items-center justify-center mx-auto mb-2">
          {(group.name || '?').substring(0, 2).toUpperCase()}
        </div>
        <div className="text-center text-sm font-bold text-gray-800">{group.name}</div>
        <div className="text-center text-[10px] text-gray-500">{group.member_count} members</div>
        {group.description && <div className="text-[11px] text-gray-600 mt-2 text-center italic">{group.description}</div>}
      </div>
      {stats && (
        <div className="p-3 border-b border-gray-100">
          <div className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">Delivery Stats</div>
          <div className="grid grid-cols-2 gap-1.5">
            <div className="bg-gray-100 rounded p-1.5 text-center">
              <div className="text-[9px] text-gray-500 uppercase font-bold">Sent</div>
              <div className="text-base font-bold text-gray-800">{stats.sent}</div>
            </div>
            <div className="bg-emerald-50 rounded p-1.5 text-center">
              <div className="text-[9px] text-emerald-600 uppercase font-bold">Delivered</div>
              <div className="text-base font-bold text-emerald-700">{stats.delivered}</div>
            </div>
            <div className="bg-amber-50 rounded p-1.5 text-center">
              <div className="text-[9px] text-amber-700 uppercase font-bold">Pending</div>
              <div className="text-base font-bold text-amber-800">{stats.pending}</div>
            </div>
            <div className="bg-rose-50 rounded p-1.5 text-center">
              <div className="text-[9px] text-rose-700 uppercase font-bold">&gt;24h</div>
              <div className="text-base font-bold text-rose-800">{stats.pending_24h}</div>
            </div>
          </div>
          <div className="mt-2 text-center">
            <div className="text-[10px] text-gray-500">Overall</div>
            <div className="text-lg font-bold text-emerald-700">{stats.delivery_percent}%</div>
          </div>
        </div>
      )}
      <div className="p-3 border-b border-gray-100 flex flex-col gap-1.5">
        {isPrivileged && (
          <button onClick={onOpenBulkPaste} className="text-[11px] px-2 py-1.5 rounded bg-violet-600 hover:bg-violet-700 text-white font-bold flex items-center justify-center gap-1" data-testid="chat-bulk-paste-btn">
            <UploadSimple size={12} weight="bold" /> Bulk Paste Dispatch
          </button>
        )}
        <button onClick={onRefresh} className="text-[11px] px-2 py-1.5 rounded bg-white border border-gray-300 hover:bg-gray-50 flex items-center justify-center gap-1">
          <ArrowClockwise size={12} /> Refresh
        </button>
      </div>
      <div className="p-3 flex-1 overflow-y-auto">
        <div className="text-[10px] font-bold uppercase text-gray-500 mb-1.5">Members</div>
        <div className="space-y-1">
          {(group.members || []).map(m => (
            <div key={m.user_id} className="flex items-center gap-1.5 text-[11px]">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[9px] ${m.role === 'admin' ? 'bg-violet-500' : 'bg-gray-400'}`}>
                {(m.user_name || '?').substring(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 truncate">{m.user_name}</div>
              {m.role === 'admin' && <span className="text-[9px] bg-violet-100 text-violet-800 rounded px-1 font-bold">ADMIN</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Bulk paste dialog ── */
function BulkPasteDialog({ open, onClose, groupId, onDispatched }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {agency_name, mobile_count, matched_group, confidence}
  const [overrideGid, setOverrideGid] = useState('');

  // Debounced live preview
  useEffect(() => {
    if (!open) { setPreview(null); setOverrideGid(''); return; }
    if (!text.trim()) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post('/chat/bulk-paste/preview', { text });
        setPreview(data);
      } catch { setPreview(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [text, open]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const target = overrideGid || preview?.matched_group?.id || groupId;
      const body = { text };
      if (target) body.group_id = target;
      const { data } = await api.post('/chat/bulk-paste', body);
      toast.success(`✅ ${data.dispatched} dispatched to ${data.group?.name} · ${data.duplicates} duplicates auto-removed`);
      setText('');
      onDispatched();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk paste failed');
    } finally { setBusy(false); }
  };

  const canDispatch = text.trim() && preview?.mobile_count > 0 && (overrideGid || preview?.matched_group?.id || groupId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-1.5"><UploadSimple size={16} /> Bulk Paste Dispatch</DialogTitle></DialogHeader>
        <div className="text-xs text-gray-600 bg-emerald-50 border border-emerald-200 rounded p-2">
          <b>Format:</b> First line = Agency name (e.g. "Navyug Indane"). Following lines = mobile numbers (10-digit).
          Duplicates within 24h auto-removed. DAC / rate columns ignored.
        </div>
        <Textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={10}
          placeholder={"Navyug Indane\n8295918552\n9079298602\n8053524839"}
          className="font-mono text-xs"
          data-testid="chat-bulk-paste-textarea"
        />
        {preview && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 space-y-1.5 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Agency detected:</span>
              <b className="text-violet-800">{preview.agency_name || <span className="italic text-gray-400">— (none, will use current group)</span>}</b>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Mobile numbers:</span>
              <b className="text-emerald-700">{preview.mobile_count}</b>
              {preview.sample_mobiles && preview.sample_mobiles.length > 0 && (
                <span className="text-[10px] text-gray-500 font-mono truncate">({preview.sample_mobiles.join(', ')}{preview.mobile_count > 5 ? '…' : ''})</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Target group:</span>
              {preview.matched_group ? (
                <>
                  <b className="text-emerald-700">{preview.matched_group.name}</b>
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 rounded px-1 font-bold">✓ auto-matched · {preview.confidence}%</span>
                </>
              ) : groupId ? (
                <b className="text-amber-700">Current group</b>
              ) : (
                <b className="text-rose-700">No match — pick a group below</b>
              )}
            </div>
            {!preview.matched_group && !groupId && (
              <div className="pt-1 border-t border-gray-200">
                <GroupPicker value={overrideGid} onChange={setOverrideGid} />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !canDispatch} data-testid="chat-bulk-paste-submit">
            {busy ? 'Dispatching…' : `Dispatch ${preview?.mobile_count || 0} mobile${preview?.mobile_count === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupPicker({ value, onChange }) {
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    api.get('/chat/groups').then(({ data }) => setGroups(data || [])).catch(() => { /* empty */ });
  }, []);
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full h-8 border border-gray-300 rounded px-2 text-xs" data-testid="chat-bulk-group-picker">
      <option value="">— Choose group —</option>
      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
    </select>
  );
}

/* ── New group dialog ── */
function NewGroupDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      api.get('/agencies').then(({ data }) => setAgencies(data || [])).catch(() => { /* empty */ });
    }
  }, [open]);
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post('/chat/groups', { name: name.trim(), description, agency_id: agencyId });
      toast.success('Group created');
      onCreated(data);
      setName(''); setDescription(''); setAgencyId('');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Group</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] font-bold text-gray-700 uppercase">Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Agency name…" data-testid="chat-new-group-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-700 uppercase">Link to Agency (optional)</label>
            <select value={agencyId} onChange={e => setAgencyId(e.target.value)} className="w-full h-9 border border-gray-300 rounded px-2 text-sm">
              <option value="">— None —</option>
              {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-700 uppercase">Description (optional)</label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Any note…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={create} disabled={busy || !name.trim()} data-testid="chat-new-group-submit">{busy ? 'Creating…' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Members Management Dialog ── */
function MembersManagementDialog({ open, onClose, group, currentUserId, isPrivileged, onChanged, onOpenAdd }) {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(null); // user_id currently being mutated
  if (!group) return null;
  const members = group.members || [];
  const filtered = members.filter(m => !query || (m.user_name || '').toLowerCase().includes(query.toLowerCase()));
  const iAmAdmin = members.some(m => m.user_id === currentUserId && m.role === 'admin');
  const canManage = isPrivileged || iAmAdmin;

  const toggleRole = async (m) => {
    if (!canManage || busy) return;
    const newRole = m.role === 'admin' ? 'member' : 'admin';
    setBusy(m.user_id);
    try {
      await api.patch(`/chat/groups/${group.id}/members/${m.user_id}/role`, { role: newRole });
      toast.success(newRole === 'admin' ? `Promoted ${m.user_name}` : `Demoted ${m.user_name}`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Role change fail'); }
    finally { setBusy(null); }
  };
  const removeM = async (m) => {
    if (!canManage || busy) return;
    if (!window.confirm(`Remove ${m.user_name} from the group?`)) return;
    setBusy(m.user_id);
    try {
      await api.delete(`/chat/groups/${group.id}/members/${m.user_id}`);
      toast.success(`Removed ${m.user_name}`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Remove fail'); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-1.5"><Users size={16} /> Group Members</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center gap-1">
          <div className="w-16 h-16 rounded-xl bg-emerald-600 text-white font-bold text-xl flex items-center justify-center">
            {(group.name || '?').substring(0, 2).toUpperCase()}
          </div>
          <div className="text-sm font-bold">{group.name}</div>
          <div className="text-[11px] text-gray-500">{members.length} members</div>
        </div>
        <div className="flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-full px-2 py-1">
          <MagnifyingGlass size={12} className="text-gray-400" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search members…" className="bg-transparent outline-none text-xs flex-1" data-testid="chat-members-search" />
        </div>
        {canManage && (
          <button onClick={onOpenAdd} className="text-xs px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-700 text-white font-bold flex items-center justify-center gap-1" data-testid="chat-add-members-btn">
            <Plus size={12} weight="bold" /> Add Members
          </button>
        )}
        <div className="text-[10px] font-bold uppercase text-gray-500 mt-1">Members · {filtered.length}</div>
        <div className="max-h-80 overflow-y-auto -mx-1 px-1 space-y-1">
          {filtered.map(m => (
            <div key={m.user_id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50" data-testid={`chat-member-${m.user_id}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[11px] ${m.role === 'admin' ? 'bg-violet-500' : 'bg-gray-400'}`}>
                {(m.user_name || '?').substring(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-gray-800 truncate">
                  {m.user_name} {m.user_id === currentUserId && <span className="text-[9px] text-gray-500 font-normal">(You)</span>}
                </div>
                <div className="text-[10px] text-gray-500">{m.role === 'admin' ? 'Admin' : 'Member'}</div>
              </div>
              {m.role === 'admin' && <span className="text-[9px] bg-violet-100 text-violet-800 rounded px-1.5 py-0.5 font-bold">ADMIN</span>}
              {canManage && m.user_id !== currentUserId && (
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={() => toggleRole(m)}
                    disabled={busy === m.user_id}
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${m.role === 'admin' ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100' : 'bg-violet-50 border-violet-300 text-violet-800 hover:bg-violet-100'}`}
                    title={m.role === 'admin' ? 'Demote to member' : 'Promote to admin'}
                    data-testid={`chat-role-toggle-${m.user_id}`}
                  >
                    {m.role === 'admin' ? 'Demote' : 'Make Admin'}
                  </button>
                  <button
                    onClick={() => removeM(m)}
                    disabled={busy === m.user_id}
                    className="w-6 h-6 flex items-center justify-center rounded-full bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200"
                    title="Remove from group"
                    data-testid={`chat-remove-${m.user_id}`}
                  >
                    <Trash size={10} />
                  </button>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center text-xs text-gray-400 py-6">No members match</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Add Members picker Dialog ── */
function AddMembersDialog({ open, onClose, groupId, existingIds, onAdded }) {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get('/chat/users/searchable', { params: { q: query || undefined } })
        .then(({ data }) => setUsers(data || []))
        .catch(() => { /* empty */ });
    }, 180);
    return () => clearTimeout(t);
  }, [open, query]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const submit = async () => {
    if (!groupId || selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/chat/groups/${groupId}/members`, { user_ids: Array.from(selected) });
      toast.success(`✅ Added ${data.added} member${data.added === 1 ? '' : 's'}`);
      onAdded();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Add fail'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-1.5"><Plus size={14} weight="bold" /> Add Members</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-full px-2 py-1">
          <MagnifyingGlass size={12} className="text-gray-400" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search users by name or email…" className="bg-transparent outline-none text-xs flex-1" data-testid="chat-add-members-search" />
        </div>
        <div className="text-[10px] text-gray-500">Selected: <b className="text-violet-700">{selected.size}</b> users</div>
        <div className="max-h-72 overflow-y-auto -mx-1 px-1 space-y-0.5 border border-gray-100 rounded">
          {users.map(u => {
            const already = existingIds.has(u.id);
            const sel = selected.has(u.id);
            return (
              <label
                key={u.id}
                className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer ${already ? 'opacity-50 cursor-not-allowed' : sel ? 'bg-violet-50' : 'hover:bg-gray-50'}`}
                data-testid={`chat-pick-user-${u.id}`}
              >
                <input
                  type="checkbox"
                  checked={sel}
                  disabled={already}
                  onChange={() => !already && toggle(u.id)}
                  className="accent-violet-600"
                />
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-[10px] ${sel ? 'bg-violet-500' : 'bg-gray-400'}`}>
                  {(u.name || '?').substring(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-gray-800 truncate">{u.name}</div>
                  <div className="text-[10px] text-gray-500 truncate">{u.email} · <span className="font-mono">{u.role}</span></div>
                </div>
                {already && <span className="text-[9px] bg-emerald-100 text-emerald-800 rounded px-1 font-bold">✓ In Group</span>}
              </label>
            );
          })}
          {users.length === 0 && <div className="text-center text-xs text-gray-400 py-6">No users</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || selected.size === 0} data-testid="chat-add-members-submit">
            {busy ? 'Adding…' : `Add ${selected.size} Member${selected.size === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Top Toolbar — shows on /chat/messages (replaces main sidebar) ── */
function ChatTopToolbar({ onOpenBulkPaste, isPrivileged, isAdmin }) {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const items = [
    { key: 'home', label: 'Home', Icon: House, go: () => nav('/chat'), show: true },
    { key: 'admin', label: 'Admin', Icon: ShieldCheck, go: () => nav('/'), show: isPrivileged },
    { key: 'complaints', label: 'Complaints', Icon: Tray, go: () => nav('/complaints-hub'), show: true },
    { key: 'lpg', label: 'LPG Report', Icon: Flame, go: () => nav('/reports'), show: isPrivileged },
    { key: 'bank', label: 'Bank', Icon: Bank, go: () => nav('/bank-statements'), show: isPrivileged },
    { key: 'bulk-paste', label: 'Bulk Paste', Icon: Clipboard, go: onOpenBulkPaste, show: isPrivileged },
    { key: 'bulk-resend', label: 'Bulk Resend', Icon: PaperPlaneTilt, go: () => nav('/booking-files'), show: isPrivileged },
    { key: 'wallet', label: 'Wallet', Icon: Wallet, go: () => nav('/chat/ledger'), show: isAdmin },
    { key: 'logins', label: 'Delivery Logins', Icon: Key, go: () => nav('/distributors'), show: isPrivileged },
  ].filter(i => i.show);

  const handleLogout = async () => {
    try { await logout(); nav('/login'); } catch { /* empty */ }
  };

  return (
    <div className="bg-emerald-900 border-b border-emerald-800 shadow-md h-14 flex items-center px-3 gap-2 shrink-0" data-testid="chat-top-toolbar">
      {/* Brand */}
      <button onClick={() => nav('/chat')} className="text-white font-bold text-sm tracking-tight hover:text-emerald-200 mr-2 shrink-0" data-testid="chat-tool-brand">
        AXIAL<span className="text-emerald-300">UP</span>
      </button>

      {/* Shortcut pills — horizontal scroll on narrow screens */}
      <div className="flex items-center gap-1 overflow-x-auto flex-1 scrollbar-hide">
        {items.map(it => {
          const { Icon } = it;
          return (
            <button
              key={it.key}
              onClick={it.go}
              className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-emerald-100 hover:bg-emerald-800 hover:text-white transition-colors text-xs font-bold"
              title={it.label}
              data-testid={`chat-tool-${it.key}`}
            >
              <Icon size={14} weight="bold" />
              <span className="hidden lg:inline">{it.label}</span>
            </button>
          );
        })}
      </div>

      {/* Right-side user controls */}
      <div className="flex items-center gap-2 shrink-0 border-l border-emerald-800 pl-2 ml-2">
        <div className="text-right hidden md:block">
          <div className="text-[11px] font-bold text-white leading-tight">{user?.name}</div>
          <div className="text-[9px] uppercase text-emerald-300 leading-tight">{user?.role}</div>
        </div>
        <div className="w-8 h-8 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs">
          {(user?.name || '?').substring(0, 1).toUpperCase()}
        </div>
        <button onClick={handleLogout} className="w-8 h-8 rounded-full hover:bg-emerald-800 flex items-center justify-center text-emerald-200 hover:text-white" title="Logout" data-testid="chat-tool-logout">
          <SignOut size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

/* ── Main Chat Page ── */
export default function ChatPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPrivileged = ['admin', 'owner', 'assistant', 'accountant'].includes(user?.role);
  const isAdmin = ['admin', 'owner'].includes(user?.role);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [input, setInput] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showSeedConfirm, setShowSeedConfirm] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [sending, setSending] = useState(false);
  const [pendingBanner, setPendingBanner] = useState(false);
  const [attachFile, setAttachFile] = useState(null);  // File object pending upload
  const [attachCaption, setAttachCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);  // { url, name }
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const listRef = useRef(null);

  const loadGroups = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/groups', { params: { search: search || undefined, filter: filter || undefined } });
      setGroups(data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Groups load fail'); }
  }, [search, filter]);

  const loadMessages = useCallback(async (gid) => {
    if (!gid) return;
    try {
      const { data } = await api.get(`/chat/groups/${gid}/messages`);
      setMessages(data);
      const { data: s } = await api.get(`/chat/groups/${gid}/stats`);
      setStats(s);
      setPendingBanner(s.pending_24h > 0);
      // Mark read
      await api.post(`/chat/groups/${gid}/mark-read`).catch(() => { /* empty */ });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Messages load fail'); }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { if (activeGroup) loadMessages(activeGroup.id); }, [activeGroup, loadMessages]);

  // Auto-select first group if URL has ?action=bulk-paste and no group yet picked
  useEffect(() => {
    if (searchParams.get('action') === 'bulk-paste' && !activeGroup && groups.length > 0) {
      setActiveGroup(groups[0]);
    }
  }, [searchParams, groups, activeGroup]);

  // Auto-open Bulk Paste dialog when ?action=bulk-paste is present AND a group is active
  useEffect(() => {
    if (searchParams.get('action') === 'bulk-paste' && activeGroup && isPrivileged && !showBulkPaste) {
      setShowBulkPaste(true);
      // Clear the query param so refresh doesn't re-open
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, activeGroup, isPrivileged, showBulkPaste, setSearchParams]);

  // Polling — refresh active group every 5s
  useEffect(() => {
    if (!activeGroup) return;
    const t = setInterval(() => { loadMessages(activeGroup.id); }, 5000);
    const g = setInterval(() => { loadGroups(); }, 15000);
    return () => { clearInterval(t); clearInterval(g); };
  }, [activeGroup, loadMessages, loadGroups]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, activeGroup]);

  const send = async () => {
    if (!input.trim() || !activeGroup || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/groups/${activeGroup.id}/messages`, {
        type: 'text',
        content: input.trim(),
        reply_to_id: replyTo?.id || '',
      });
      setInput('');
      setReplyTo(null);
      await loadMessages(activeGroup.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Send fail'); }
    finally { setSending(false); }
  };

  const handleStatusCycle = useCallback(async (mid, newStatus) => {
    try {
      await api.patch(`/chat/messages/${mid}/delivery`, { status: newStatus });
      if (activeGroup) loadMessages(activeGroup.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Status change fail'); }
  }, [activeGroup, loadMessages]);

  const handleReact = async (msg, emoji) => {
    try {
      await api.post(`/chat/messages/${msg.id}/reactions`, { emoji });
      loadMessages(activeGroup.id);
    } catch { toast.error('React fail'); }
  };

  const handleDelete = async (msg, scope = 'everyone') => {
    const confirmMsg = scope === 'me'
      ? 'Delete this message only for you? Baaki group members ko dikhti rahegi.'
      : 'Delete this message for EVERYONE? Sabke screen se hat jaayegi.';
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.delete(`/chat/messages/${msg.id}`, { params: { scope } });
      loadMessages(activeGroup.id);
      toast.success(scope === 'me' ? 'Hidden for you' : 'Deleted for everyone');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete fail');
    }
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10 MB');
      return;
    }
    setAttachFile(f);
    setAttachCaption('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadAttachment = async () => {
    if (!attachFile || !activeGroup || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', attachFile);
      fd.append('caption', attachCaption.trim());
      if (replyTo?.id) fd.append('reply_to_id', replyTo.id);
      await api.post(`/chat/groups/${activeGroup.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachFile(null);
      setAttachCaption('');
      setReplyTo(null);
      await loadMessages(activeGroup.id);
      toast.success('File sent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/chat/seed-from-agencies');
      toast.success(`✅ ${data.created} new groups · ${data.already_exists} already existed`);
      setShowSeedConfirm(false);
      await loadGroups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Seed fail');
    } finally { setSeeding(false); }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100" data-testid="chat-page">
      <ChatTopToolbar
        onOpenBulkPaste={() => {
          if (!activeGroup) {
            if (groups.length > 0) setActiveGroup(groups[0]);
            setTimeout(() => setShowBulkPaste(true), 200);
          } else {
            setShowBulkPaste(true);
          }
        }}
        isPrivileged={isPrivileged}
        isAdmin={isAdmin}
      />
      <div className="flex flex-1 min-h-0">      <GroupsList
        groups={groups}
        activeGroupId={activeGroup?.id}
        onSelect={setActiveGroup}
        filter={filter}
        setFilter={setFilter}
        search={search}
        setSearch={setSearch}
        onNewGroup={() => setShowNewGroup(true)}
        isPrivileged={isPrivileged}
      />

      {/* Center */}
      <div className="flex-1 flex flex-col bg-emerald-50/30">
        {!activeGroup ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <UsersThree size={64} weight="thin" className="text-gray-300" />
            <div className="text-sm mt-2">Select a group to start chatting</div>
            {isPrivileged && groups.length === 0 && (
              <button onClick={() => setShowSeedConfirm(true)} className="mt-4 text-xs px-4 py-2 rounded-full bg-violet-600 text-white hover:bg-violet-700 font-bold shadow-sm" data-testid="chat-seed-btn">
                Auto-create groups from Agencies →
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-600 text-white font-bold flex items-center justify-center">
                  {(activeGroup.name || '?').substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-800">{activeGroup.name}</div>
                  <button
                    onClick={() => setShowMembers(true)}
                    className="text-[10px] text-gray-500 hover:text-violet-700 hover:underline cursor-pointer flex items-center gap-0.5"
                    data-testid="chat-open-members"
                  >
                    <span className="font-bold">{activeGroup.member_count}</span> members
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {stats && (
                  <div className="flex items-center gap-1 text-[10px] mr-2 border border-gray-200 rounded-full px-2 py-0.5">
                    <span className="text-gray-500">Sent</span> <b>{stats.sent}</b>
                    <span className="text-gray-300">|</span>
                    <span className="text-emerald-600">Delivered</span> <b className="text-emerald-700">{stats.delivered}</b>
                    <span className="text-gray-300">|</span>
                    <span className="text-amber-600">Pending</span> <b className="text-amber-700">{stats.pending}</b>
                  </div>
                )}
                <button onClick={() => setShowInfo(v => !v)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="chat-toggle-info">
                  <Users size={14} className="text-gray-600" />
                </button>
              </div>
            </div>

            {/* Urgent banner */}
            {pendingBanner && (
              <div className="bg-rose-100 border-b border-rose-300 px-3 py-1.5 flex items-center gap-2" data-testid="chat-urgent-banner">
                <Bell size={13} weight="fill" className="text-rose-600" />
                <div className="text-[11px] text-rose-800">
                  <b>URGENT</b> · {stats.pending_24h} pending &gt;24h · click a card to mark delivered
                </div>
              </div>
            )}

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%224%22 height=%224%22><rect width=%224%22 height=%224%22 fill=%22%23f9fafb%22/></svg>')]">
              {messages.length === 0 && (
                <div className="text-center text-xs text-gray-400 py-8">No messages yet. Send the first one.</div>
              )}
              {(() => {
                // Group consecutive lpg_card messages sharing the same batch_id
                // Also insert date separators between distinct dates
                const rendered = [];
                let i = 0;
                let lastDay = '';
                const pushDateSep = (iso) => {
                  const dk = dayKey(iso);
                  if (dk && dk !== lastDay) {
                    rendered.push(<DateSeparator key={`date-${dk}-${rendered.length}`} label={fmtDateLabel(iso)} />);
                    lastDay = dk;
                  }
                };
                while (i < messages.length) {
                  const m = messages[i];
                  pushDateSep(m.created_at);
                  if (m.type === 'lpg_card' && m.batch_id) {
                    const batch = [m];
                    let j = i + 1;
                    while (j < messages.length && messages[j].type === 'lpg_card' && messages[j].batch_id === m.batch_id) {
                      batch.push(messages[j]);
                      j += 1;
                    }
                    if (batch.length >= 2) {
                      batch.sort((a, b) => (a.batch_order || 0) - (b.batch_order || 0));
                      rendered.push(
                        <div key={m.batch_id} className={`flex ${m.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                          <LpgBatchBubble batchMsgs={batch} onStatusCycle={handleStatusCycle} />
                        </div>
                      );
                      i = j;
                      continue;
                    }
                  }
                  rendered.push(
                    <MessageRow
                      key={m.id}
                      msg={m}
                      own={m.sender_id === user?.id}
                      currentUserId={user?.id}
                      currentUserRole={user?.role}
                      activeGroup={activeGroup}
                      onReply={setReplyTo}
                      onReact={handleReact}
                      onDelete={handleDelete}
                      onCopy={() => { /* empty */ }}
                      onStatusCycle={handleStatusCycle}
                      onOpenLightbox={(url, name) => setLightbox({ url, name })}
                    />
                  );
                  i += 1;
                }
                return rendered;
              })()}
              {stats && stats.duplicates_24h > 0 && (
                <div className="mx-auto max-w-sm bg-amber-100 border border-amber-300 rounded-lg px-3 py-2 text-[11px] text-amber-800 text-center" data-testid="chat-dup-banner">
                  <b>DUPLICATE AUTO-REMOVED · {stats.duplicates_24h}</b> in last 24h
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Reply preview */}
            {replyTo && (
              <div className="bg-emerald-50 border-t border-emerald-200 px-3 py-1.5 flex items-center gap-2">
                <div className="flex-1 border-l-2 border-emerald-500 pl-2">
                  <div className="text-[10px] text-emerald-700 font-bold">Reply to {replyTo.sender_name}</div>
                  <div className="text-[11px] text-gray-700 truncate">{replyTo.content || `[${replyTo.type}]`}</div>
                </div>
                <button onClick={() => setReplyTo(null)} className="text-gray-500 hover:text-gray-700"><X size={12} /></button>
              </div>
            )}

            {/* Input */}
            <div className="bg-white border-t border-gray-200 px-3 py-2 flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,.pdf,.xlsx,.xls,.csv,.docx,.doc,.txt,.zip"
                onChange={onFilePicked}
                data-testid="chat-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-emerald-50 border border-gray-200 text-gray-700 flex items-center justify-center"
                title="Attach file (image/pdf/excel/csv)"
                data-testid="chat-attach-btn"
              >
                <Paperclip size={14} weight="bold" />
              </button>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder="Type a message… use @ to mention · Shift+Enter for new line"
                className="flex-1 border border-gray-300 rounded-2xl px-3 py-1.5 text-xs resize-none focus:outline-none focus:border-emerald-500 max-h-24"
                data-testid="chat-input"
              />
              <button onClick={send} disabled={sending || !input.trim()} className="w-9 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white flex items-center justify-center shadow-sm" data-testid="chat-send-btn">
                <PaperPlaneRight size={14} weight="fill" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Right panel */}
      {activeGroup && showInfo && (
        <GroupInfoPanel
          group={activeGroup}
          stats={stats}
          onClose={() => setShowInfo(false)}
          onOpenBulkPaste={() => setShowBulkPaste(true)}
          isPrivileged={isPrivileged}
          onRefresh={() => loadMessages(activeGroup.id)}
        />
      )}

      {/* Modals */}
      <BulkPasteDialog open={showBulkPaste} onClose={() => setShowBulkPaste(false)} groupId={activeGroup?.id} onDispatched={() => { loadMessages(activeGroup?.id); loadGroups(); }} />
      <NewGroupDialog open={showNewGroup} onClose={() => setShowNewGroup(false)} onCreated={(g) => { loadGroups(); setActiveGroup(g); }} />
      <MembersManagementDialog
        open={showMembers}
        onClose={() => setShowMembers(false)}
        group={activeGroup}
        currentUserId={user?.id}
        isPrivileged={isPrivileged}
        onChanged={async () => {
          // Refresh active group (fetch latest members)
          try {
            const { data } = await api.get(`/chat/groups/${activeGroup.id}`);
            setActiveGroup(data);
          } catch { /* empty */ }
          loadGroups();
        }}
        onOpenAdd={() => setShowAddMembers(true)}
      />
      <AddMembersDialog
        open={showAddMembers}
        onClose={() => setShowAddMembers(false)}
        groupId={activeGroup?.id}
        existingIds={new Set((activeGroup?.members || []).map(m => m.user_id))}
        onAdded={async () => {
          try {
            const { data } = await api.get(`/chat/groups/${activeGroup.id}`);
            setActiveGroup(data);
          } catch { /* empty */ }
          loadGroups();
        }}
      />

      <Dialog open={showSeedConfirm} onOpenChange={setShowSeedConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Seed Groups from Agencies?</DialogTitle></DialogHeader>
          <div className="text-xs text-gray-600 space-y-1.5">
            <p>Ye action har active agency ke liye ek chat group create karega, sabhi admin/assistant/CA users ko auto-add karega, aur distributor ko attach karega.</p>
            <p><b>Existing groups skip honge</b> — sirf naye create honge.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSeedConfirm(false)} disabled={seeding}>Cancel</Button>
            <Button onClick={handleSeed} disabled={seeding} data-testid="chat-seed-confirm">{seeding ? 'Seeding…' : 'Yes, seed all agencies'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File preview dialog (WhatsApp-style — thumbnail + caption input) */}
      {attachFile && (
        <Dialog open onOpenChange={() => !uploading && setAttachFile(null)}>
          <DialogContent data-testid="file-preview-dialog" className="max-w-md">
            <DialogHeader><DialogTitle>Send File</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <AttachmentPreview file={attachFile} />
              <textarea
                value={attachCaption}
                onChange={e => setAttachCaption(e.target.value)}
                placeholder="Add a caption (optional)…"
                rows={2}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-500"
                data-testid="attach-caption-input"
                autoFocus
              />
              <div className="text-[11px] text-gray-500">
                Size: {fmtBytes(attachFile.size)} · Type: {attachFile.type || 'unknown'}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAttachFile(null)} disabled={uploading}>Cancel</Button>
              <Button onClick={uploadAttachment} disabled={uploading} data-testid="attach-send-btn">
                {uploading ? 'Sending…' : 'Send'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Image lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="image-lightbox"
        >
          <img src={lightbox.url} alt={lightbox.name || ''} className="max-w-full max-h-full object-contain" />
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-900 flex items-center justify-center shadow-lg"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="lightbox-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

/* ── Attachment preview helper ── */
function AttachmentPreview({ file }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!file) return;
    if (file.type?.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [file]);
  if (preview) {
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center max-h-72">
        <img src={preview} alt="preview" className="max-h-72 w-auto" />
      </div>
    );
  }
  // File icon
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const cat = ['pdf'].includes(ext) ? 'pdf' : ['xlsx', 'xls'].includes(ext) ? 'excel'
    : ext === 'csv' ? 'csv' : ['doc', 'docx'].includes(ext) ? 'word' : ext === 'zip' ? 'zip' : 'file';
  const iconMap = {
    pdf: { Icon: FilePdf, color: 'text-rose-600', bg: 'bg-rose-50' },
    excel: { Icon: FileXls, color: 'text-emerald-700', bg: 'bg-emerald-50' },
    csv: { Icon: FileCsv, color: 'text-emerald-700', bg: 'bg-emerald-50' },
    word: { Icon: FileDoc, color: 'text-blue-700', bg: 'bg-blue-50' },
    zip: { Icon: FileZip, color: 'text-amber-700', bg: 'bg-amber-50' },
    file: { Icon: FileIcon, color: 'text-gray-700', bg: 'bg-gray-50' },
  };
  const { Icon, color, bg } = iconMap[cat];
  return (
    <div className={`rounded-lg border border-gray-200 ${bg} p-6 flex flex-col items-center justify-center gap-2`}>
      <Icon size={48} weight="duotone" className={color} />
      <div className="text-sm font-semibold text-gray-900 text-center break-all">{file.name}</div>
    </div>
  );
}
